import { useState } from "react";
import Button from "./Button";
import Spinner from "./Spinners";
import { Tag } from "./Tag";
import { RegenerateCampaignsModal } from "./RegenerateCampaignsModal";
import {
  useCampaignRegeneration,
  useCompetitorAdsPlan,
  useDraftCompetitorKeywords,
  usePushCompetitorKeywords,
  useRegenerateCampaigns,
} from "../helpers/hooks/use-competitor-keywords";
import type {
  CampaignRegenerationState,
  CompetitorAdsCoverage,
  CompetitorAdsPlan,
  DraftedKeyword,
  KeywordPushResult,
} from "../types/competitors";

const CARD_CLASS = "rounded-md border border-border-primary px-4 py-3 text-sm";
const CARD_LABEL_CLASS =
  "text-xs font-medium text-content-secondary uppercase mb-2";
const SECTION_CLASS = "flex flex-col gap-2 rounded-md bg-neutral-50 px-3 py-2";

const keywordId = (k: DraftedKeyword) => `${k.keyword}|${k.matchType}`;

function errorMessage(error: unknown): string {
  return (
    (error as any)?.response?.data?.message ??
    (error as Error)?.message ??
    "Request failed"
  );
}

function generatedOn(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "";
}

/** One line on where the campaigns stand, from the account's point of view. */
function campaignStatusLine(
  regen: CampaignRegenerationState,
  plan: CompetitorAdsPlan | undefined,
): string {
  switch (regen.published) {
    case "paused":
      return `${regen.campaigns.length} campaigns published — paused, never served.`;
    case "serving":
      return "Campaigns are live.";
    case "unmanaged":
      return `${regen.campaigns.length} campaigns published — not all created by the pipeline.`;
    default:
      if (plan?.status === "no-ads-account") return "No Google Ads account yet.";
      if (!regen.generatedAt) return "No campaigns generated yet.";
      return `Campaigns generated ${generatedOn(regen.generatedAt)} — not in Google Ads yet.`;
  }
}

function regenerateLabel(regen: CampaignRegenerationState): string {
  if (regen.campaigns.length > 0) return "Regenerate & republish";
  return regen.generatedAt ? "Regenerate campaigns" : "Generate campaigns";
}

function regenerateNote(regen: CampaignRegenerationState): string {
  if (regen.campaigns.length > 0) {
    return `Supersedes the ${regen.campaigns.length} campaigns in the account — renamed, status unchanged. You'll see the details before it runs.`;
  }
  return regen.publishesOnCompletion
    ? "Rebuilds the stored campaigns from the saved answers; the pipeline publishes them when generation finishes."
    : "Rebuilds the stored campaigns from the saved answers; they publish when the customer finishes onboarding.";
}

function RegenerateBlock({
  userId,
  regen,
  dirty,
}: {
  userId: string;
  regen: CampaignRegenerationState;
  dirty: boolean;
}) {
  const regenerate = useRegenerateCampaigns(userId);
  const [confirming, setConfirming] = useState(false);

  if (regen.generating) {
    return (
      <div className="flex items-center gap-2 text-content-secondary">
        <Spinner className="!text-brand-primary" />
        Regenerating campaigns — a few minutes; this card refreshes itself.
      </div>
    );
  }
  if (regen.blockedReason) {
    return (
      <div className="text-xs text-content-secondary italic">
        {regen.blockedReason}
      </div>
    );
  }

  const needsConfirm = regen.campaigns.length > 0;
  const run = () =>
    regenerate.mutate(undefined, { onSettled: () => setConfirming(false) });

  return (
    <div className={SECTION_CLASS}>
      <div className="text-xs text-content-secondary">
        {regenerateNote(regen)}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          size="sm"
          variant={needsConfirm ? "outline" : "default"}
          loading={regenerate.isPending && !confirming}
          disabled={dirty}
          onClick={needsConfirm ? () => setConfirming(true) : run}
        >
          {regenerateLabel(regen)}
        </Button>
        {dirty && (
          <span className="text-xs text-content-secondary">
            Save your changes first — generation reads the saved answers.
          </span>
        )}
      </div>
      {confirming && (
        <RegenerateCampaignsModal
          state={regen}
          pending={regenerate.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={run}
        />
      )}
    </div>
  );
}

function coverageTag(row: CompetitorAdsCoverage) {
  if (row.chain) return <Tag tone="neutral">chain — not bid on</Tag>;
  if (row.matchedKeywords.length > 0) return <Tag tone="green">in ads</Tag>;
  return <Tag tone="amber">not in ads</Tag>;
}

function missingNames(plan: CompetitorAdsPlan): string[] {
  return plan.competitors
    .filter((row) => !row.chain && row.matchedKeywords.length === 0)
    .map((row) => row.name);
}

function CoverageList({ rows }: { rows: CompetitorAdsCoverage[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row) => (
        <div key={row.name} className="flex items-start gap-2 flex-wrap">
          <span className="text-sm text-content-primary">{row.name}</span>
          {coverageTag(row)}
          {row.matchedKeywords.length > 0 && (
            <span className="text-xs text-content-secondary font-mono">
              {row.matchedKeywords.join(" · ")}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function DraftReview({
  drafts,
  selected,
  onToggle,
}: {
  drafts: DraftedKeyword[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  if (drafts.length === 0) {
    return (
      <div className="text-xs text-content-secondary italic">
        Nothing new to add — every keyword the generator built is already live.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-xs text-content-secondary">
        Proposed keywords — untick any you don't want pushed:
      </div>
      {drafts.map((draft) => {
        const id = keywordId(draft);
        return (
          <label key={id} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.has(id)}
              onChange={() => onToggle(id)}
              className="w-4 h-4 accent-brand-primary"
            />
            <span className="font-mono text-sm text-content-primary">
              {draft.keyword}
            </span>
            <span className="text-xs text-content-secondary">
              {draft.matchType.toLowerCase()} · for {draft.competitorName}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function PushOutcome({ result }: { result: KeywordPushResult }) {
  return (
    <div className="text-xs text-content-secondary flex flex-col gap-0.5">
      {result.created.map((row) => (
        <div key={row.resourceName}>
          Added <span className="font-mono">{row.keyword}</span>
        </div>
      ))}
      {result.skipped.map((row) => (
        <div key={`skip-${row.keyword}`}>
          Skipped <span className="font-mono">{row.keyword}</span> — {row.reason}
        </div>
      ))}
      {result.failed.map((row) => (
        <div key={`fail-${row.keyword}`} className="text-red-700">
          Rejected <span className="font-mono">{row.keyword}</span> — {row.error}
        </div>
      ))}
    </div>
  );
}

/**
 * The keyword-level way in for a campaign that already exists: which saved
 * competitors the live ad group covers, a draft of keywords for the rest,
 * and a push of only the drafts the operator left ticked.
 */
function KeywordSync({
  userId,
  plan,
  dirty,
}: {
  userId: string;
  plan: CompetitorAdsPlan;
  dirty: boolean;
}) {
  const draft = useDraftCompetitorKeywords(userId);
  const push = usePushCompetitorKeywords(userId);
  const [drafts, setDrafts] = useState<DraftedKeyword[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<KeywordPushResult | null>(null);

  const missing = missingNames(plan);
  const startDraft = () =>
    draft.mutate(missing, {
      onSuccess: (keywords) => {
        setDrafts(keywords);
        setSelected(new Set(keywords.map(keywordId)));
        setOutcome(null);
      },
    });
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const discardDrafts = () => {
    setDrafts(null);
    setSelected(new Set());
  };
  const chosen = (drafts ?? []).filter((k) => selected.has(keywordId(k)));
  const commit = () =>
    push.mutate(chosen, {
      onSuccess: (result) => {
        setOutcome(result);
        discardDrafts();
      },
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-content-secondary">
        {plan.campaignName} · {plan.adGroupName} · {plan.liveKeywords.length}{" "}
        live keyword{plan.liveKeywords.length === 1 ? "" : "s"}
      </div>

      <CoverageList rows={plan.competitors} />

      {missing.length > 0 && drafts === null && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            loading={draft.isPending}
            disabled={dirty}
            onClick={startDraft}
          >
            Draft keywords for {missing.length} competitor
            {missing.length === 1 ? "" : "s"}
          </Button>
          {dirty && (
            <span className="text-xs text-content-secondary">
              Save your changes first — drafting reads the saved set.
            </span>
          )}
        </div>
      )}

      {drafts !== null && (
        <div className={`${SECTION_CLASS} gap-3`}>
          <DraftReview drafts={drafts} selected={selected} onToggle={toggle} />
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={discardDrafts}
              disabled={push.isPending}
            >
              Discard
            </Button>
            {drafts.length > 0 && (
              <Button
                size="sm"
                loading={push.isPending}
                disabled={chosen.length === 0 || dirty}
                onClick={commit}
              >
                Push {chosen.length} to Google Ads
              </Button>
            )}
          </div>
        </div>
      )}

      {outcome && <PushOutcome result={outcome} />}

      {missing.length === 0 && drafts === null && (
        <div className="text-xs text-content-secondary">
          Every saved competitor has a live keyword.
        </div>
      )}
    </div>
  );
}

/**
 * Where the hotel's campaigns stand against its saved answers, and the two
 * ways to bring them in step. Regenerating rebuilds everything from the
 * saved answers — the usual path, for a hotel that has not gone live — and
 * only ever sets aside pipeline campaigns that never served. The keyword
 * draft is the surgical path once campaigns exist, and the only one once
 * they have served. Every step is explicit because a publish is spend.
 */
export function CompetitorAdsCard({
  userId,
  dirty,
}: {
  userId: string;
  /** Unsaved edits in the editor — both paths read the saved answers, so wait. */
  dirty: boolean;
}) {
  const regen = useCampaignRegeneration(userId);
  const plan = useCompetitorAdsPlan(userId);
  const loading = regen.isLoading || plan.isLoading;

  return (
    <div className={CARD_CLASS}>
      <div className={CARD_LABEL_CLASS}>Google Ads</div>

      {loading && (
        <div className="flex items-center gap-2 text-content-secondary">
          <Spinner className="!text-brand-primary" />
          Reading the campaigns…
        </div>
      )}

      {!loading && (regen.error || plan.error) && (
        <div className="text-xs text-red-700">
          Could not read the Google Ads account:{" "}
          {errorMessage(regen.error ?? plan.error)}
        </div>
      )}

      {!loading && regen.data && (
        <div className="flex flex-col gap-3">
          <div className="text-sm text-content-primary">
            {campaignStatusLine(regen.data, plan.data)}
          </div>

          <RegenerateBlock userId={userId} regen={regen.data} dirty={dirty} />

          {plan.data?.status === "ready" && (
            <KeywordSync userId={userId} plan={plan.data} dirty={dirty} />
          )}

          <p className="text-xs text-content-secondary italic">
            Pushing keywords only adds them; a competitor removed here keeps its
            live keywords. Regenerating supersedes existing campaigns by
            renaming them — nothing is deleted or paused. Ad copy changes
            (USP, amenities) only reach Google Ads through a regeneration.
          </p>
        </div>
      )}
    </div>
  );
}
